<template>
    <v-card tile class="drawer-content elevation-0 pb-12">

        <v-card-title class="sansation_subtitle primary lighten-2 info--text font-weight-bold py-6 d-flex justify-space-between">
            <div class="d-inline">
                Merge Users
            </div>
            <div class="d-inline">
                <v-btn
                    icon
                    outlined
                    color="info"
                    elevation="1"
                    @click="onClose">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
        </v-card-title>

        <v-card-text class="mt-6 px-4">
            <div v-if="loading" class="text-center py-12">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <div v-else>
                <v-alert v-if="error_message" type="error" color="error darken-1" dense outlined>{{ error_message }}</v-alert>
                <v-alert v-for="(blocker, ndx) in blockers" :key="`b${ndx}`" type="error" color="error darken-1" dense outlined>{{ blocker }}</v-alert>
                <v-alert v-for="(warning, ndx) in warnings" :key="`w${ndx}`" type="warning" color="warning darken-1" dense outlined>{{ warning }}</v-alert>

                <div class="sansation font-weight-bold mb-2 mt-6">1. Choose the user to keep</div>
                <p class="avenir_book font-weight-thin mb-6">
                    Projects, questionnaire answers, documents, listing matches and activity from the other users move to the kept user.
                    The other users are set inactive and their emails are saved on the kept user as merged emails (they can't be used to log in).
                </p>

                <v-radio-group 
                    v-model="keep_user_id" 
                    class="mt-0" 
                    hide-details
                >
                    <v-card
                        v-for="user in users"
                        :key="user.user_id"
                        outlined
                        class="mb-3 px-3 py-4 avenir_book"
                        :class="{ 'keep-selected': user.user_id === keep_user_id }"
                        @click="keep_user_id = user.user_id">
                        <div class="d-flex align-start">
                            <v-radio :value="user.user_id" color="primary" class="mt-1"></v-radio>
                            <div class="flex-grow-1">
                                <div class="d-flex justify-space-between">
                                    <div>
                                        <strong class="avenir_book">{{ user.first_name }} {{ user.last_name }}</strong>
                                        &middot; {{ user.user_email }}
                                        <v-chip v-if="user.user_id === keep_user_id" x-small color="primary" dark class="ml-2">KEEP</v-chip>
                                        <v-chip v-else x-small outlined color="accent" class="ml-2">MERGE &amp; DEACTIVATE</v-chip>
                                    </div>
                                    <div class="caption">
                                        Created {{ formatDate(user.created_date) }}
                                        &middot; Last login {{ user.last_logged_in ? formatDate(user.last_logged_in) : 'never' }}
                                        &middot; {{ user.is_active ? 'Active' : 'Inactive' }}
                                    </div>
                                </div>
                                <div class="caption">
                                    {{ [user.phone, user.company, user.title, [user.city, user.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || 'No contact details' }}
                                    <span v-if="user.merged_emails && user.merged_emails.length"> &middot; Merged emails: {{ user.merged_emails.join(', ') }}</span>
                                </div>
                                <div class="caption mt-1">
                                    {{ user.projects.length }} owned project{{ user.projects.length === 1 ? '' : 's' }}
                                    &middot; linked to {{ user.counts.linked_projects }} other
                                    &middot; {{ user.counts.docs }} doc{{ user.counts.docs === 1 ? '' : 's' }}
                                    &middot; {{ user.counts.listing_matches }} listing match{{ user.counts.listing_matches === 1 ? '' : 'es' }}
                                </div>
                                <div v-for="project in user.projects" :key="project.project_id" class="caption grey--text text--darken-1">
                                    &bull; {{ project.module_name }} &ndash; {{ project.status || 'no status' }}
                                    <span v-if="project.is_archived">(archived)</span>
                                    &ndash; {{ project.answer_count }} answers, {{ project.doc_count }} docs
                                </div>
                            </div>
                        </div>
                    </v-card>
                </v-radio-group>

                <div v-if="buyer_profiles.length > 1">
                    <div class="senation font-weight-bold mt-4 mb-2">2. Choose the Buyer Platform profile to keep</div>
                    <p class="avenir_book mb-2">
                        The other profile{{ buyer_profiles.length > 2 ? 's' : '' }} and {{ buyer_profiles.length > 2 ? 'their' : 'its' }}
                        questionnaire answers will be <strong>deleted</strong>. Documents and activity on {{ buyer_profiles.length > 2 ? 'them' : 'it' }}
                        move to the kept profile. Listing matches are combined; where both users matched the same listing, the kept profile's match wins.
                    </p>
                    <v-radio-group 
                        v-model="keep_buyer_project_id" 
                        class="mt-0" 
                        hide-details
                    >
                        <v-radio
                            v-for="profile in buyer_profiles"
                            :key="profile.project_id"
                            :value="profile.project_id"
                            color="primary">
                            <template v-slot:label>
                                <span>
                                    {{ ownerEmail(profile.user_id) }} &ndash; {{ profile.status || 'no status' }},
                                    started {{ formatDate(profile.date_initialized) }},
                                    {{ profile.answer_count }} answers, {{ profile.doc_count }} docs
                                </span>
                            </template>
                        </v-radio>
                    </v-radio-group>
                </div>

                <v-checkbox
                    v-model="confirmed"
                    color="primary"
                    class="mt-6 avenir_book"
                    hide-details
                    :label="`I understand ${users.length - 1} user${users.length - 1 === 1 ? '' : 's'} will be merged into ${keepEmail}. You can undo this from the user's Merge History.`">
                </v-checkbox>
            </div>
        </v-card-text>

        <v-card-actions class="px-4">
            <v-spacer></v-spacer>
            <v-btn
                color="primary lighten-2 info--text"
                class="elevation-0"
                :disabled="!canMerge"
                :loading="merging"
                @click="mergeUsers">
                Merge Users
            </v-btn>
            <v-btn 
                outlined
                color="primary"
                @click="onClose">Cancel</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
    import axios from "axios";
    import Cookie from 'js-cookie';
    import appUrls from '../helpers/environment';

    export default {
        name: 'MergeUsers',

        props: {
            UserIds: { type: Array, default: () => [] }
        },

        data: () => ({
            loading: false,
            merging: false,
            confirmed: false,
            error_message: '',
            users: [],
            buyer_profiles: [],
            blockers: [],
            warnings: [],
            keep_user_id: null,
            keep_buyer_project_id: null
        }),

        computed: {
            keepEmail () {
                const keep = this.users.find(u => u.user_id === this.keep_user_id)
                return keep ? keep.user_email : 'the kept user'
            },
            canMerge () {
                return !this.loading
                    && !this.blockers.length
                    && this.users.length > 1
                    && !!this.keep_user_id
                    && (this.buyer_profiles.length < 2 || !!this.keep_buyer_project_id)
                    && this.confirmed
            }
        },

        watch: {
            UserIds: {
                immediate: true,
                handler () { this.getPreview() }
            },
            keep_user_id () {
                this.confirmed = false
                // DEFAULT TO THE KEPT USER'S OWN BUYER PROFILE WHEN THEY HAVE ONE
                const own = this.buyer_profiles.find(p => p.user_id === this.keep_user_id)
                if ( own ) this.keep_buyer_project_id = own.project_id
            }
        },

        methods: {
            getPreview: async function () {
                this.error_message = ''
                this.confirmed = false
                if ( this.UserIds.length < 2 ) return

                this.loading = true
                try {
                    const preview_rs = await axios.post(`${appUrls.api}/api/admin/merge_users/preview`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        user_ids: this.UserIds
                    })

                    if ( preview_rs.data.outcome ) {
                        this.users = preview_rs.data.users
                        this.buyer_profiles = preview_rs.data.buyer_profiles
                        this.blockers = preview_rs.data.blockers
                        this.warnings = preview_rs.data.warnings
                        this.keep_buyer_project_id = null
                        // DEFAULT: KEEP THE OLDEST ACTIVE USER
                        const keep = this.users.find(u => u.is_active) || this.users[0]
                        this.keep_user_id = keep ? keep.user_id : null
                    } else {
                        this.error_message = preview_rs.data.message
                    }
                } catch ( error ) {
                    console.log(error)
                    this.error_message = 'There was an issue loading these users.'
                }
                this.loading = false
            },

            mergeUsers: async function () {
                this.merging = true
                this.error_message = ''
                try {
                    const merge_rs = await axios.post(`${appUrls.api}/api/admin/merge_users`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        keep_user_id: this.keep_user_id,
                        merge_user_ids: this.users.map(u => u.user_id).filter(id => id !== this.keep_user_id),
                        keep_buyer_project_id: this.buyer_profiles.length > 1 ? this.keep_buyer_project_id : null
                    })

                    if ( merge_rs.data.outcome ) {
                        this.$emit('merged', merge_rs.data)
                    } else {
                        this.error_message = merge_rs.data.message
                    }
                } catch ( error ) {
                    console.log(error)
                    this.error_message = (error.response && error.response.data && error.response.data.message) || 'The merge failed and no changes were made.'
                }
                this.merging = false
            },

            ownerEmail ( user_id ) {
                const owner = this.users.find(u => u.user_id === user_id)
                return owner ? owner.user_email : user_id
            },

            formatDate ( value ) {
                return value ? String(value).substr(0, 10) : ''
            },

            onClose () {
                this.$emit('closeMerge')
            }
        }
    }
</script>

<style scoped>
    .keep-selected {
        border-color: var(--v-primary-base) !important;
        border-width: 2px !important;
    }
    .drawer-content {
        height: 100%; /* Full height of the drawer */
        overflow-y: auto; /* Allow vertical scrolling */
        background-color: whitesmoke !important;
    }
    /* Hide scrollbar but allow scrolling */
    .drawer-content::-webkit-scrollbar {
        display: none; /* For Chrome, Safari, and Edge */
    }
    .drawer-content {
        -ms-overflow-style: none; /* For Internet Explorer and Edge */
        scrollbar-width: none; /* For Firefox */
    }
</style>



// WEBPACK FOOTER //
// src/components/MergeUsers.vue